import React, { useState, useEffect } from 'react';
import { Award, Eye, DollarSign, TrendingUp, ToggleLeft, ToggleRight, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/constants';




// Helper Component: Stat Card
const StatCard = ({ icon: Icon, label, value, trend }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {trend && (
            <p className={`text-sm mt-2 ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
              {trend.positive ? '↗' : '↘'} {trend.value}
            </p>
          )}
        </div>
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
    </div>
  );
};

// Helper Component: Featured Listings Table
const FeaturedListingsTable = ({ listings, onCancel }) => {
  if (!listings || listings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">No active featured listings</p>
      </div>
    );
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Gig': return '💼';
      case 'User': return '👤';
      case 'Request': return '📋';
      default: return '⭐';
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      gold: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white',
      premium: 'bg-gradient-to-r from-purple-400 to-purple-600 text-white',
      standard: 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${colors[tier] || colors.standard}`}>
        {tier?.toUpperCase() || 'STANDARD'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 'Never';
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? `${days} days` : 'Expired';
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Started
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expires
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {listings.map((listing) => (
              <tr key={listing.id || listing._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-2xl">{getTypeIcon(listing.targetType)}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                    {listing.targetTitle || 'Untitled'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getTierBadge(listing.tier)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{listing.userName}</div>
                  <div className="text-sm text-gray-500">{listing.userEmail}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(listing.startDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(listing.expiresAt)}</div>
                  <div className="text-xs text-gray-500">{getDaysRemaining(listing.expiresAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  GH₵{listing.amount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onCancel(listing.id || listing._id)}
                    className="text-red-600 hover:text-red-900 ml-4"
                    title="Cancel Subscription"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Helper Component: Pricing Config
const PricingConfig = ({ config, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [localConfig, setLocalConfig] = useState(config);

  const handleSave = async () => {
    try {
      onChange(localConfig);
      toast.success('Pricing configuration updated');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Pricing Configuration</h2>
        {editing ? (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {['standard', 'premium', 'gold'].map((tier) => (
          <div key={tier} className="border rounded-lg p-4">
            <h3 className="font-semibold capitalize mb-4">{tier}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Monthly (GH₵)</label>
                <input
                  type="number"
                  value={localConfig.pricingTiers?.[tier]?.monthly || 0}
                  onChange={(e) => {
                    setLocalConfig({
                      ...localConfig,
                      pricingTiers: {
                        ...localConfig.pricingTiers,
                        [tier]: {
                          ...localConfig.pricingTiers[tier],
                          monthly: Number(e.target.value)
                        }
                      }
                    });
                  }}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Annual (GH₵)</label>
                <input
                  type="number"
                  value={localConfig.pricingTiers?.[tier]?.annual || 0}
                  onChange={(e) => {
                    setLocalConfig({
                      ...localConfig,
                      pricingTiers: {
                        ...localConfig.pricingTiers,
                        [tier]: {
                          ...localConfig.pricingTiers[tier],
                          annual: Number(e.target.value)
                        }
                      }
                    });
                  }}
                  disabled={!editing}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Search Multiplier</label>
          <input
            type="number"
            step="0.1"
            value={localConfig.boostMultiplier?.search || 1.5}
            onChange={(e) => {
              setLocalConfig({
                ...localConfig,
                boostMultiplier: {
                  ...localConfig.boostMultiplier,
                  search: Number(e.target.value)
                }
              });
            }}
            disabled={!editing}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">AI Matching Bonus (0-1)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={localConfig.boostMultiplier?.aiMatching || 0.3}
            onChange={(e) => {
              setLocalConfig({
                ...localConfig,
                boostMultiplier: {
                  ...localConfig.boostMultiplier,
                  aiMatching: Number(e.target.value)
                }
              });
            }}
            disabled={!editing}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>
      </div>
    </div>
  );
};

// Main Component
const FeaturedManagement = () => {
  const [config, setConfig] = useState({
    featureEnabled: false,
    pricingTiers: {
      standard: { monthly: 50, annual: 500 },
      premium: { monthly: 100, annual: 1000 },
      gold: { monthly: 200, annual: 2000 }
    },
    maxFeaturedPerPage: 10,
    boostMultiplier: {
      search: 1.5,
      aiMatching: 0.3
    }
  });

  const [stats, setStats] = useState({
    activeFeatured: 0,
    monthlyRevenue: 0,
    impressionsBoost: 0,
    conversionRate: 0
  });

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
    fetchStats();
    fetchListings();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/featured/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConfig(response.data.config || config);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/featured/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data.stats || stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchListings = async () => {
    try {
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/featured/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async () => {
    try {
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/admin/featured/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setConfig({ ...config, featureEnabled: response.data.enabled });
      toast.success(`Featured listings ${response.data.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      toast.error('Failed to toggle feature');
    }
  };

  const handleCancelListing = async (listingId) => {
    if (!confirm('Are you sure you want to cancel this featured listing?')) return;

    try {
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/admin/featured/cancel/${listingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Featured listing cancelled');
      fetchListings();
      fetchStats();
    } catch (error) {
      console.error('Failed to cancel listing:', error);
      toast.error('Failed to cancel listing');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Featured Listings Control</h1>

        {/* MASTER TOGGLE */}
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Feature Status:</span>
          <button
            onClick={toggleFeature}
            className={`px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors ${config.featureEnabled
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
              }`}
          >
            {config.featureEnabled ? (
              <>
                <ToggleRight className="w-5 h-5" />
                🟢 ACTIVE
              </>
            ) : (
              <>
                <ToggleLeft className="w-5 h-5" />
                🔴 INACTIVE
              </>
            )}
          </button>
        </div>
      </div>

      {!config.featureEnabled && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-800">
            <strong>Feature Disabled:</strong> Users cannot purchase featured listings.
            All existing featured items remain active until expiry.
          </p>
        </div>
      )}

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Award}
          label="Active Featured"
          value={stats.activeFeatured}
        />
        <StatCard
          icon={DollarSign}
          label="Revenue (Month)"
          value={`GH₵${stats.monthlyRevenue.toFixed(2)}`}
        />
        <StatCard
          icon={Eye}
          label="Impressions Boost"
          value={`+${stats.impressionsBoost}%`}
        />
        <StatCard
          icon={TrendingUp}
          label="Conversion Rate"
          value={`${stats.conversionRate}%`}
        />
      </div>

      {/* Active Featured Listings Table */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Active Featured Listings</h2>
        <FeaturedListingsTable listings={listings} onCancel={handleCancelListing} />
      </div>

      {/* Pricing Configuration */}
      <PricingConfig config={config} onChange={setConfig} />
    </div>
  );
};

export default FeaturedManagement;